import DataTable from '@/components/DataTable.jsx';
import { usePastSeasons } from './useLeague.js';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });
const nf = new Intl.NumberFormat('en-GB');

/* ---- Managers -------------------------------------------------- */

/*
 * The 13-column table that was the worst thing on the site to read on a
 * phone: it sat inside the .wide-content 100vw escape hatch and broke out
 * of the page. It scrolls inside its own container now.
 *
 * Movement is shown as an arrow against last week's position rather than
 * colouring the whole row green or red, which previously drowned out the
 * fixture and position colours elsewhere on the page.
 */
function movement(row) {
	const delta = (row.last_position ?? row.position) - row.position;
	if (!delta) return <span className="move move--flat" title="No change">–</span>;
	return delta > 0 ? (
		<span className="move move--up" title={`Up ${delta}`}>▲{delta}</span>
	) : (
		<span className="move move--down" title={`Down ${-delta}`}>▼{-delta}</span>
	);
}

const managerColumns = (showPlayed) =>
	[
		{
			key: 'position',
			label: 'Pos',
			title: 'League position',
			numeric: true,
			defaultSort: 'asc',
			render: (pos, row) => (
				<span className="position-cell">
					<span className="position-cell__num">{pos}</span>
					{movement(row)}
				</span>
			),
		},
		{
			key: 'Player',
			label: 'Manager',
			strong: true,
			render: (name, row) =>
				row.Link ? (
					<a href={row.Link} target="_blank" rel="noreferrer">
						{name}
					</a>
				) : (
					name
				),
		},
		{ ...num('OR', 'Overall rank'), render: (v) => (v ? nf.format(v) : '—') },
		{ ...num('TP', 'Total pts', 'Total points'), defaultSort: 'desc' },
		{
			...num('Score', 'GW pts', 'Gameweek points, with the transfer cost in brackets'),
			/* Always bracketed, including (0), as the old table did. Showing it
			   only when a hit was taken hid the column's meaning in any week
			   where nobody in the league took one. */
			render: (v, row) => `${v} (${row.TC ? -row.TC : 0})`,
		},
		showPlayed && num('num_played', 'Played', 'Players who have played'),
		{ key: 'Captain', label: 'Captain' },
		/* Out of the filter: searching a player name is asking who captained
		   them, and vice-captain matches are noise against that. */
		{ key: 'VC', label: 'Vice', title: 'Vice-captain', searchable: false },
		!showPlayed && { key: 'chips_played', label: 'Chips', title: 'Chips played' },
		{ key: 'Chip', label: 'Active chip' },
		num('custom_points', 'Form', 'Form points'),
		num('Bench', 'Bench', 'Points left on the bench'),
		{ ...num('TV', 'Value', 'Team value'), render: (v) => `£${v}` },
	].filter(Boolean);

export function ManagersTab({ standings, showPlayed }) {
	return (
		<DataTable
			columns={managerColumns(showPlayed)}
			rows={standings ?? []}
			rowKey={(r) => r.fpl_id ?? r.Player}
			filterPlaceholder="Filter by manager, captain or chip"
			maxHeight="70vh"
		/>
	);
}

/* ---- Captains and effective ownership --------------------------- */

/* These three arrive as positional arrays, not objects. */
export function CaptainsTab({ captains }) {
	const rows = (captains ?? []).map(([player, captaincy, eo]) => ({
		player,
		captaincy,
		eo,
	}));
	return (
		<DataTable
			columns={[
				{ key: 'player', label: 'Player', strong: true },
				{
					...num('captaincy', 'Captaincy', 'Share of the league captaining them'),
					defaultSort: 'desc',
					render: (v) => `${v}%`,
				},
				{
					...num('eo', 'Effective ownership'),
					render: (v) => `${v}%`,
				},
			]}
			rows={rows}
			rowKey={(r) => r.player}
			filterPlaceholder="Filter by player"
			maxWidth="34rem"
		/>
	);
}

export function ChipsTab({ chips }) {
	const rows = (chips ?? []).map(([criteria, percent]) => ({ criteria, percent }));
	return (
		<DataTable
			/* No default sort: the payload holds two groups — how many chips
			   managers played, then which chips they played — and sorting by
			   percentage shuffles them together. */
			columns={[
				{ key: 'criteria', label: 'Criteria', strong: true },
				{
					...num('percent', 'Managers', 'Share of the league'),
					render: (v) => `${v}%`,
				},
			]}
			rows={rows}
			rowKey={(r) => r.criteria}
			filterable={false}
			maxWidth="30rem"
		/>
	);
}

/* ---- Team selections -------------------------------------------- */

export function SelectionsTab({ selection }) {
	return (
		<DataTable
			columns={[
				{ key: 'name', label: 'Player', strong: true },
				{ key: 'team', label: 'Team' },
				{ key: 'position', label: 'Pos', title: 'Position', tone: (v) => v },
				{ ...num('price', 'Cost'), render: (v) => `£${v}` },
				{ ...num('fplOwnership', 'FPL TSB', 'Teams selected by, all of FPL'), render: (v) => `${v}%` },
				{
					...num('leagueOwnership', 'League TSB', 'Teams selected by, this league'),
					defaultSort: 'desc',
					render: (v) => `${v}%`,
				},
				num('livePoints', 'GW pts', 'Gameweek points'),
			]}
			rows={selection ?? []}
			rowKey={(r) => `${r.name}-${r.team}`}
			filterPlaceholder="Filter by player, team or position"
			maxHeight="70vh"
		/>
	);
}

/* ---- Past seasons ------------------------------------------------ */

export function PastSeasonsTab({ leagueName, rows: initialRows, label: initialLabel }) {
	const season = usePastSeasons(leagueName, initialRows, initialLabel);

	return (
		<div className="past-seasons">
			<div className="past-seasons__nav">
				<button
					className="btn btn--secondary btn--icon"
					type="button"
					onClick={() => season.go('prev')}
					disabled={season.loading}
					aria-label="Previous season"
				>
					←
				</button>
				<span className="past-seasons__label">{season.label ?? '—'}</span>
				<button
					className="btn btn--secondary btn--icon"
					type="button"
					onClick={() => season.go('next')}
					disabled={season.loading}
					aria-label="Next season"
				>
					→
				</button>
				{season.note && <span className="past-seasons__note">{season.note}</span>}
			</div>

			<DataTable
				columns={[
					{ key: 'manager_name', label: 'Manager', strong: true },
					{ ...num('rank', 'Overall rank'), render: (v) => nf.format(v) },
					{ ...num('total_points', 'Total points'), defaultSort: 'desc', render: (v) => nf.format(v) },
				]}
				rows={season.rows}
				rowKey={(r) => r.manager_name}
				filterPlaceholder="Filter by manager"
				maxWidth="40rem"
				emptyMessage="No data recorded for this season."
			/>
		</div>
	);
}

/* ---- All-time ---------------------------------------------------- */

export function AllTimeTab({ hofRank }) {
	return (
		<DataTable
			columns={[
				{ key: 'player', label: 'Manager', strong: true },
				num('total_seasons', 'Seasons', 'Seasons played'),
				num('top1k', 'Top 1k', 'Top 1k finishes'),
				num('top10k', 'Top 10k', 'Top 10k finishes'),
				num('top100k', 'Top 100k', 'Top 100k finishes'),
				{ ...num('best_rank', 'Best'), render: (v) => nf.format(v) },
				{ ...num('median', 'Median'), render: (v) => nf.format(v) },
				{
					...num('hof', 'Weighted rank', 'Weighted all-time rank'),
					defaultSort: 'asc',
					render: (v) => nf.format(v),
				},
			]}
			rows={hofRank ?? []}
			rowKey={(r) => r.player}
			filterPlaceholder="Filter by manager"
			maxHeight="70vh"
		/>
	);
}
